import { getLabel, TLDConfigProperty, encoder, NormalizeBytesEncoder, DateEncoder, RpcRequest, RpcResponse, RecordMetadata, RpcRequestData, hexToArray, SmartContractType, getLevel, getTld, BytesEncoder, RpcResponseData, DomainNameValidationResult, NotSupportedError } from '@tezos-domains/core';
import BigNumber from 'bignumber.js';
import { Schema } from '@taquito/michelson-encoder';
import { packDataBytes } from '@taquito/michel-codec';
import { sha512 } from 'sha.js';
import { MapEncoder, BigNumberEncoder } from '@tezos-domains/taquito';

var DomainAcquisitionState;
(function (DomainAcquisitionState) {
    DomainAcquisitionState["Unobtainable"] = "Unobtainable";
    DomainAcquisitionState["Taken"] = "Taken";
    DomainAcquisitionState["CanBeBought"] = "CanBeBought";
    DomainAcquisitionState["CanBeAuctioned"] = "CanBeAuctioned";
    DomainAcquisitionState["AuctionInProgress"] = "AuctionInProgress";
    DomainAcquisitionState["CanBeSettled"] = "CanBeSettled";
    DomainAcquisitionState["CanBeClaimed"] = "CanBeClaimed";
})(DomainAcquisitionState || (DomainAcquisitionState = {}));
class DomainAcquisitionInfo {
    constructor(_state, _auctionInfo, _buyOrRenewInfo, _unobtainableInfo) {
        this._state = _state;
        this._auctionInfo = _auctionInfo;
        this._buyOrRenewInfo = _buyOrRenewInfo;
        this._unobtainableInfo = _unobtainableInfo;
    }
    get acquisitionState() {
        return this._state;
    }
    get auctionDetails() {
        this.assertState('auctionDetails', DomainAcquisitionState.CanBeAuctioned, DomainAcquisitionState.AuctionInProgress, DomainAcquisitionState.CanBeSettled);
        return this._auctionInfo;
    }
    get buyOrRenewDetails() {
        this.assertState('buyOrRenewDetails', DomainAcquisitionState.CanBeBought, DomainAcquisitionState.Taken);
        return this._buyOrRenewInfo;
    }
    get unobtainableDetails() {
        this.assertState('unobtainableDetails', DomainAcquisitionState.Unobtainable);
        return this._unobtainableInfo;
    }
    /**
     * Calculates buy or renew price for this domain.
     *
     * @param duration The number of days for which to calculate the price.
     * @returns Price for owning the domain for the specified duration in mutez.
     */
    calculatePrice(duration) {
        this.assertState('calculatePrice', DomainAcquisitionState.CanBeBought, DomainAcquisitionState.Taken);
        return this._buyOrRenewInfo.pricePerMinDuration * (duration / this._buyOrRenewInfo.minDuration);
    }
    /** @internal */
    static createUnobtainable(unobtainableInfo) {
        return new DomainAcquisitionInfo(DomainAcquisitionState.Unobtainable, undefined, undefined, unobtainableInfo);
    }
    /** @internal */
    static createAuction(state, auctionInfo) {
        return new DomainAcquisitionInfo(state, auctionInfo);
    }
    /** @internal */
    static createBuyOrRenew(state, fifsInfo) {
        return new DomainAcquisitionInfo(state, undefined, fifsInfo);
    }
    /** @internal */
    static createClaimable() {
        return new DomainAcquisitionInfo(DomainAcquisitionState.CanBeClaimed);
    }
    assertState(description, ...allowedStates) {
        if (!allowedStates.includes(this._state)) {
            throw new Error(`${description} is only available for states ${allowedStates.join(', ')}.`);
        }
    }
}
function calculateAcquisitionInfo(input) {
    let launchDate = undefined;
    if (input.tldConfiguration.isClaimable) {
        return createClaimableInfo();
    }
    const now = new Date();
    const label = getLabel(input.name);
    const minDuration = input.tldConfiguration.minDuration.toNumber();
    const defaultStandardPriceIndex = parseInt(TLDConfigProperty.DEFAULT_STANDARD_PRICE);
    let standardPricePerDay = input.tldConfiguration.standardPrices[defaultStandardPriceIndex + label.length];
    if (!standardPricePerDay) {
        standardPricePerDay = input.tldConfiguration.standardPrices[defaultStandardPriceIndex];
    }
    const minBid = getPricePerMinDuration(standardPricePerDay);
    if (input.existingDomain && (!input.existingDomain.expiry || input.existingDomain.expiry > now)) {
        return createBuyOrRenewInfo(DomainAcquisitionState.Taken);
    }
    const defaultLaunchDateIndex = parseInt(TLDConfigProperty.DEFAULT_LAUNCH_DATE);
    launchDate = input.tldConfiguration.launchDates[(defaultLaunchDateIndex + label.length).toString()];
    if (launchDate === undefined) {
        launchDate = input.tldConfiguration.launchDates[defaultLaunchDateIndex.toString()];
    }
    if (!launchDate) {
        return createUnobtainableInfo();
    }
    const minBidIncreaseCoef = input.tldConfiguration.minBidIncreaseRatio.dividedBy(100).plus(1).toNumber();
    const minAuctionPeriod = input.tldConfiguration.minAuctionPeriod.times(1000).toNumber();
    const bidAdditionalPeriod = input.tldConfiguration.bidAdditionalPeriod.times(1000).toNumber();
    if (input.existingAuction) {
        const maxSettlementDate = input.existingAuction.ownedUntil;
        if (now >= maxSettlementDate) {
            const maxNewAuctionDate = new Date(maxSettlementDate.getTime() + minAuctionPeriod);
            if (now < maxNewAuctionDate) {
                return createAuctionInfo(DomainAcquisitionState.CanBeAuctioned, maxNewAuctionDate, minBid);
            }
            else {
                return createBuyOrRenewInfo(DomainAcquisitionState.CanBeBought);
            }
        }
        else if (now < input.existingAuction.endsAt) {
            return createAuctionInfo(DomainAcquisitionState.AuctionInProgress, input.existingAuction.endsAt, Math.round((input.existingAuction.lastBid * minBidIncreaseCoef) / 1e5) * 1e5, input.existingAuction.lastBid, input.existingAuction.lastBidder);
        }
        else {
            return createAuctionInfo(DomainAcquisitionState.CanBeSettled, input.existingAuction.endsAt, NaN, input.existingAuction.lastBid, input.existingAuction.lastBidder);
        }
    }
    else {
        if (now < launchDate) {
            return createUnobtainableInfo();
        }
        const periodEndDate = input.existingDomain
            ? new Date(input.existingDomain.expiry.getTime() + minAuctionPeriod)
            : new Date(launchDate.getTime() + minAuctionPeriod);
        if (now < periodEndDate) {
            return createAuctionInfo(DomainAcquisitionState.CanBeAuctioned, periodEndDate, minBid);
        }
        else {
            return createBuyOrRenewInfo(DomainAcquisitionState.CanBeBought);
        }
    }
    function createAuctionInfo(state, auctionEnd, nextMinimumBid, lastBid, lastBidder) {
        return DomainAcquisitionInfo.createAuction(state, {
            auctionEnd,
            nextMinimumBid,
            registrationDuration: minDuration,
            lastBid: lastBid == null ? 0 : lastBid,
            lastBidder: lastBidder || null,
            bidAdditionalPeriod,
        });
    }
    function createBuyOrRenewInfo(state) {
        return DomainAcquisitionInfo.createBuyOrRenew(state, { pricePerMinDuration: minBid, minDuration });
    }
    function createUnobtainableInfo() {
        return DomainAcquisitionInfo.createUnobtainable({ launchDate: launchDate || null });
    }
    function createClaimableInfo() {
        return DomainAcquisitionInfo.createClaimable();
    }
    function getPricePerMinDuration(pricePerDay) {
        return pricePerDay.dividedBy(1e6).multipliedBy(minDuration).decimalPlaces(0, BigNumber.ROUND_HALF_UP).toNumber();
    }
}

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}

function __metadata(metadataKey, metadataValue) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(metadataKey, metadataValue);
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

class BlockchainDomainsManager {
    constructor(tezos, tracer, operationFactory, dataProvider) {
        this.tezos = tezos;
        this.tracer = tracer;
        this.operationFactory = operationFactory;
        this.dataProvider = dataProvider;
    }
    setChildRecord(request, operationParams) {
        return __awaiter(this, void 0, void 0, function* () {
            this.tracer.trace(`=> Executing.`, request);
            const params = yield this.operationFactory.setChildRecord(request, operationParams);
            const operation = yield this.tezos.call(params);
            this.tracer.trace('<= Executed.', operation.opHash);
            return operation;
        });
    }
    updateRecord(request, operationParams) {
        return __awaiter(this, void 0, void 0, function* () {
            this.tracer.trace(`=> Executing.`, request);
            const params = yield this.operationFactory.updateRecord(request, operationParams);
            const operation = yield this.tezos.call(params);
            this.tracer.trace('<= Executed.', operation.opHash);
            return operation;
        });
    }
    commit(tld, request, operationParams) {
        return __awaiter(this, void 0, void 0, function* () {
            this.tracer.trace(`=> Executing.`, request);
            const params = yield this.operationFactory.commit(tld, request, operationParams);
            const operation = yield this.tezos.call(params);
            this.tracer.trace('<= Executed.', operation.opHash);
            return operation;
        });
    }
    buy(tld, request, operationParams) {
        return __awaiter(this, void 0, void 0, function* () {
            this.tracer.trace(`=> Executing.`, request);
            const params = yield this.operationFactory.buy(tld, request, operationParams);
            const operation = yield this.tezos.call(params);
            this.tracer.trace('<= Executed.', operation.opHash);
            return operation;
        });
    }
    renew(tld, request, operationParams) {
        return __awaiter(this, void 0, void 0, function* () {
            this.tracer.trace(`=> Executing.`, request);
            const params = yield this.operationFactory.renew(tld, request, operationParams);
            const operation = yield this.tezos.call(params);
            this.tracer.trace('<= Executed.', operation.opHash);
            return operation;
        });
    }
    claimReverseRecord(request, operationParams) {
        return __awaiter(this, void 0, void 0, function* () {
            this.tracer.trace(`=> Executing.`, request);
            const params = yield this.operationFactory.claimReverseRecord(request, operationParams);
            const operation = yield this.tezos.call(params);
            this.tracer.trace('<= Executed.', operation.opHash);
            return operation;
        });
    }
    updateReverseRecord(request, operationParams) {
        return __awaiter(this, void 0, void 0, function* () {
            this.tracer.trace(`=> Executing.`, request);
            const params = yield this.operationFactory.updateReverseRecord(request, operationParams);
            const operation = yield this.tezos.call(params);
            this.tracer.trace('<= Executed.', operation.opHash);
            return operation;
        });
    }
    getCommitment(tld, request) {
        return this.dataProvider.getCommitment(tld, request);
    }
    getAcquisitionInfo(name) {
        return this.dataProvider.getAcquisitionInfo(name);
    }
    getBidderBalance(tld, address) {
        return this.dataProvider.getBidderBalance(tld, address);
    }
    getTldConfiguration(tld) {
        return this.dataProvider.getTldConfiguration(tld);
    }
    bid(tld, request, operationParams) {
        return __awaiter(this, void 0, void 0, function* () {
            this.tracer.trace(`=> Executing.`, request);
            const params = yield this.operationFactory.bid(tld, request, operationParams);
            const operation = yield this.tezos.call(params);
            this.tracer.trace('<= Executed.', operation.opHash);
            return operation;
        });
    }
    settle(tld, request, operationParams) {
        return __awaiter(this, void 0, void 0, function* () {
            this.tracer.trace(`=> Executing.`, request);
            const params = yield this.operationFactory.settle(tld, request, operationParams);
            const operation = yield this.tezos.call(params);
            this.tracer.trace('<= Executed.', operation.opHash);
            return operation;
        });
    }
    withdraw(tld, recipient, operationParams) {
        return __awaiter(this, void 0, void 0, function* () {
            this.tracer.trace(`=> Executing withdraw.`, recipient);
            const params = yield this.operationFactory.withdraw(tld, recipient, operationParams);
            const operation = yield this.tezos.call(params);
            this.tracer.trace('<= Executed.', operation.opHash);
            return operation;
        });
    }
    getTokenId(name) {
        return this.dataProvider.getTokenId(name);
    }
    transfer(name, newOwner, operationParams) {
        return __awaiter(this, void 0, void 0, function* () {
            this.tracer.trace(`=> Executing transfer.`, name, newOwner);
            const params = yield this.operationFactory.transfer(name, newOwner, operationParams);
            const operation = yield this.tezos.call(params);
            this.tracer.trace('<= Executed.', operation.opHash);
            return operation;
        });
    }
    batch(builder) {
        return __awaiter(this, void 0, void 0, function* () {
            this.tracer.trace(`=> Executing batch.`);
            const operations = yield builder(this.operationFactory);
            this.tracer.trace('!! Operations in batch', operations);
            const operation = yield this.tezos.batch(operations);
            this.tracer.trace('<= Executed.', operation.opHash);
            return operation;
        });
    }
}

let SetChildRecordRequest = class SetChildRecordRequest {
};
__decorate([
    encoder(NormalizeBytesEncoder),
    __metadata("design:type", String)
], SetChildRecordRequest.prototype, "label", void 0);
__decorate([
    encoder(NormalizeBytesEncoder),
    __metadata("design:type", String)
], SetChildRecordRequest.prototype, "parent", void 0);
__decorate([
    encoder(MapEncoder),
    __metadata("design:type", RecordMetadata)
], SetChildRecordRequest.prototype, "data", void 0);
__decorate([
    encoder(DateEncoder),
    __metadata("design:type", Object)
], SetChildRecordRequest.prototype, "expiry", void 0);
SetChildRecordRequest = __decorate([
    RpcRequest()
], SetChildRecordRequest);
let UpdateRecordRequest = class UpdateRecordRequest {
};
__decorate([
    encoder(NormalizeBytesEncoder),
    __metadata("design:type", String)
], UpdateRecordRequest.prototype, "name", void 0);
__decorate([
    encoder(MapEncoder),
    __metadata("design:type", RecordMetadata)
], UpdateRecordRequest.prototype, "data", void 0);
UpdateRecordRequest = __decorate([
    RpcRequest()
], UpdateRecordRequest);
let CommitmentRequest = class CommitmentRequest {
};
__decorate([
    encoder(NormalizeBytesEncoder),
    __metadata("design:type", String)
], CommitmentRequest.prototype, "label", void 0);
CommitmentRequest = __decorate([
    RpcRequest()
], CommitmentRequest);
let BuyRequest = class BuyRequest {
};
__decorate([
    encoder(NormalizeBytesEncoder),
    __metadata("design:type", String)
], BuyRequest.prototype, "label", void 0);
__decorate([
    encoder(MapEncoder),
    __metadata("design:type", RecordMetadata)
], BuyRequest.prototype, "data", void 0);
BuyRequest = __decorate([
    RpcRequest()
], BuyRequest);
let RenewRequest = class RenewRequest {
};
__decorate([
    encoder(NormalizeBytesEncoder),
    __metadata("design:type", String)
], RenewRequest.prototype, "label", void 0);
RenewRequest = __decorate([
    RpcRequest()
], RenewRequest);
let ReverseRecordRequest = class ReverseRecordRequest {
};
__decorate([
    encoder(NormalizeBytesEncoder),
    __metadata("design:type", Object)
], ReverseRecordRequest.prototype, "name", void 0);
ReverseRecordRequest = __decorate([
    RpcRequest()
], ReverseRecordRequest);
let UpdateReverseRecordRequest = class UpdateReverseRecordRequest {
};
__decorate([
    encoder(NormalizeBytesEncoder),
    __metadata("design:type", Object)
], UpdateReverseRecordRequest.prototype, "name", void 0);
UpdateReverseRecordRequest = __decorate([
    RpcRequest()
], UpdateReverseRecordRequest);
let BidRequest = class BidRequest {
};
__decorate([
    encoder(NormalizeBytesEncoder),
    __metadata("design:type", String)
], BidRequest.prototype, "label", void 0);
BidRequest = __decorate([
    RpcRequest()
], BidRequest);
let SettleRequest = class SettleRequest {
};
__decorate([
    encoder(NormalizeBytesEncoder),
    __metadata("design:type", String)
], SettleRequest.prototype, "label", void 0);
__decorate([
    encoder(MapEncoder),
    __metadata("design:type", RecordMetadata)
], SettleRequest.prototype, "data", void 0);
SettleRequest = __decorate([
    RpcRequest()
], SettleRequest);
let Tzip12TransferDestination = class Tzip12TransferDestination {
};
Tzip12TransferDestination = __decorate([
    RpcRequest()
], Tzip12TransferDestination);
let Tzip12TransferRequest = class Tzip12TransferRequest {
};
Tzip12TransferRequest = __decorate([
    RpcRequest()
], Tzip12TransferRequest);
let TLDRecord = class TLDRecord {
};
__decorate([
    encoder(DateEncoder),
    __metadata("design:type", Date)
], TLDRecord.prototype, "expiry", void 0);
TLDRecord = __decorate([
    RpcResponse()
], TLDRecord);
let AuctionState = class AuctionState {
};
__decorate([
    encoder(BigNumberEncoder),
    __metadata("design:type", Number)
], AuctionState.prototype, "last_bid", void 0);
__decorate([
    encoder(DateEncoder),
    __metadata("design:type", Date)
], AuctionState.prototype, "ends_at", void 0);
__decorate([
    encoder(BigNumberEncoder),
    __metadata("design:type", Number)
], AuctionState.prototype, "ownership_period", void 0);
AuctionState = __decorate([
    RpcResponse()
], AuctionState);
let ClaimRequest = class ClaimRequest {
};
__decorate([
    encoder(NormalizeBytesEncoder),
    __metadata("design:type", String)
], ClaimRequest.prototype, "label", void 0);
__decorate([
    encoder(NormalizeBytesEncoder),
    __metadata("design:type", String)
], ClaimRequest.prototype, "tld", void 0);
ClaimRequest = __decorate([
    RpcRequest()
], ClaimRequest);
class CommitmentInfo {
    constructor(_created, _usableFrom, _usableUntil) {
        this._created = _created;
        this._usableFrom = _usableFrom;
        this._usableUntil = _usableUntil;
    }
    /**
     * Date and time when it becomes possible to buy the domain by the commitment sender
     */
    get usableFrom() {
        return this._usableFrom;
    }
    /**
     * Date and time when the commitment is expired and can no longer be used.
     */
    get usableUntil() {
        return this._usableUntil;
    }
    /**
     * Date and time when the commitment was created.
     */
    get created() {
        return this._created;
    }
    /**
     * Returns a promise that is resolved when commitment can be spent to buy a domain.
     */
    waitUntilUsable() {
        return __awaiter(this, void 0, void 0, function* () {
            yield new Promise(resolve => setTimeout(() => resolve(), this._usableFrom.getTime() - Date.now()));
        });
    }
}
const DEFAULT_STORAGE_LIMITS = {
    set_child_record: 400,
    update_record: 400,
    claim_reverse_record: 400,
    update_reverse_record: 200,
    commit: 200,
    buy: 800,
    bid: 200,
    settle: 800,
};

const commitmentSchemaExpression = {
    prim: 'pair',
    args: [
        {
            prim: 'pair',
            args: [
                {
                    annots: ['%label'],
                    prim: 'bytes',
                },
                {
                    annots: ['%owner'],
                    prim: 'address',
                },
            ],
        },
        {
            annots: ['%nonce'],
            prim: 'nat',
        }
    ],
};
class CommitmentGenerator {
    constructor() {
        this.commitmentSchema = new Schema(commitmentSchemaExpression);
    }
    generate(parameters) {
        const encodedRequest = RpcRequestData.fromObject(CommitmentRequest, parameters).encode();
        const data = this.commitmentSchema.Encode(encodedRequest);
        const packed = packDataBytes(data, commitmentSchemaExpression).bytes;
        return new sha512().update(hexToArray(packed)).digest('hex');
    }
}

class TaquitoManagerDataProvider {
    constructor(tezos, addressBook, tracer, commitmentGenerator, validator, bigMapDataProvider) {
        this.tezos = tezos;
        this.addressBook = addressBook;
        this.tracer = tracer;
        this.commitmentGenerator = commitmentGenerator;
        this.validator = validator;
        this.bigMapDataProvider = bigMapDataProvider;
    }
    getCommitment(tld, request) {
        return __awaiter(this, void 0, void 0, function* () {
            this.assertDomainWithValidTld(`${request.label}.${tld}`);
            this.tracer.trace('=> Getting existing commitment.', tld, request);
            const address = yield this.addressBook.lookup(SmartContractType.TLDRegistrar, tld);
            const commitmentHash = this.commitmentGenerator.generate(request);
            const constants = yield this.tezos.getConstants();
            const minimalBlockDelay = constants.minimal_block_delay.times(1000).toNumber();
            this.tracer.trace('!! Calculated commitment hash for given parameters.', commitmentHash);
            const commitmentResponse = yield this.tezos.getBigMapValue(address, s => s.store.commitments, RpcRequestData.fromValue(commitmentHash));
            const commitment = commitmentResponse.scalar(DateEncoder);
            if (!commitment) {
                this.tracer.trace('<= Commitment not found.');
                return null;
            }
            const tldConfiguration = yield this.getTldConfiguration(tld);
            const minAge = tldConfiguration.minCommitmentAge.times(1000).toNumber();
            const maxAge = tldConfiguration.maxCommitmentAge.times(1000).toNumber();
            const usableFrom = new Date(commitment.getTime() + Math.max(0, minAge - minimalBlockDelay));
            const usableUntil = new Date(commitment.getTime() + maxAge);
            this.tracer.trace(`<= Commitment found with timestamp ${commitment.toISOString()}. Based on TLDRegistrar it's usable from ${usableFrom.toISOString()} to ${usableUntil.toISOString()}.`);
            return new CommitmentInfo(commitment, usableFrom, usableUntil);
        });
    }
    getAcquisitionInfo(name) {
        return __awaiter(this, void 0, void 0, function* () {
            if (getLevel(name) !== 2) {
                throw new Error(`Domain '${name}' cannot be acquired. Only 2nd level domains (e.g. 'alice.${this.validator.supportedTLDs[0]}') can be acquired.`);
            }
            this.assertDomainWithValidTld(name);
            const tld = getTld(name);
            const tldConfiguration = yield this.getTldConfiguration(tld);
            if (tldConfiguration.isClaimable) {
                return DomainAcquisitionInfo.createClaimable();
            }
            const address = yield this.addressBook.lookup(SmartContractType.TLDRegistrar, tld);
            const label = getLabel(name);
            const tldRecordResponse = yield this.tezos.getBigMapValue(address, s => s.store.records, RpcRequestData.fromValue(label, BytesEncoder));
            const tldRecord = tldRecordResponse.decode(TLDRecord);
            const auctionStateResponse = yield this.tezos.getBigMapValue(address, s => s.store.auctions, RpcRequestData.fromValue(label, BytesEncoder));
            const auctionState = auctionStateResponse.decode(AuctionState);
            let existingAuction;
            if (auctionState) {
                existingAuction = {
                    endsAt: auctionState.ends_at,
                    lastBid: auctionState.last_bid,
                    lastBidder: auctionState.last_bidder,
                    ownedUntil: new Date(auctionState.ends_at.getTime() + auctionState.ownership_period * 24 * 60 * 60 * 1000),
                };
            }
            return calculateAcquisitionInfo({
                tldConfiguration,
                name: name,
                existingDomain: tldRecord ? { expiry: tldRecord.expiry } : undefined,
                existingAuction,
            });
        });
    }
    getTldConfiguration(tld) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const isClaimableTld = this.validator.isClaimableTld(tld);
            if (isClaimableTld) {
                const address = yield this.addressBook.lookup(SmartContractType.OracleRegistrar);
                const oracleStorage = yield this.tezos.storage(address);
                return {
                    isClaimable: true,
                    minCommitmentAge: new BigNumber(0),
                    maxCommitmentAge: new BigNumber(0),
                    minDuration: new BigNumber(0),
                    minAuctionPeriod: new BigNumber(0),
                    minBidIncreaseRatio: new BigNumber(0),
                    bidAdditionalPeriod: new BigNumber(0),
                    launchDates: {},
                    standardPrices: {},
                    claimPrice: oracleStorage.claim_price,
                };
            }
            const address = yield this.addressBook.lookup(SmartContractType.TLDRegistrar, tld);
            const tldStorage = yield this.tezos.storage(address);
            const config = new RpcResponseData(tldStorage.store.config).scalar(MapEncoder);
            const defaultProperties = Object.values(TLDConfigProperty);
            const otherKeys = [TLDConfigProperty.DEFAULT_LAUNCH_DATE].concat(config.keys().filter(k => !defaultProperties.includes(k)));
            const launchDateKeys = otherKeys.filter(k => isKeyRange(k, 1000, 2000));
            const standardPriceKeys = otherKeys.filter(k => isKeyRange(k, 2000, 3000));
            const launchDates = {};
            for (const launchDateKey of launchDateKeys) {
                const timestamp = (_a = config.get(launchDateKey)) === null || _a === void 0 ? void 0 : _a.toNumber();
                launchDates[launchDateKey] = timestamp ? new Date(timestamp * 1000) : null;
            }
            const standardPrices = {};
            standardPrices[TLDConfigProperty.DEFAULT_STANDARD_PRICE] = config.get(TLDConfigProperty.MIN_BID_PER_DAY);
            for (const standardPriceKey of standardPriceKeys) {
                standardPrices[standardPriceKey] = config.get(standardPriceKey);
            }
            return {
                minCommitmentAge: config.get(TLDConfigProperty.MIN_COMMITMENT_AGE),
                maxCommitmentAge: config.get(TLDConfigProperty.MAX_COMMITMENT_AGE),
                minDuration: config.get(TLDConfigProperty.MIN_DURATION),
                minAuctionPeriod: config.get(TLDConfigProperty.MIN_AUCTION_PERIOD),
                minBidIncreaseRatio: config.get(TLDConfigProperty.MIN_BID_INCREASE_RATIO),
                bidAdditionalPeriod: config.get(TLDConfigProperty.BID_ADDITIONAL_PERIOD),
                launchDates,
                standardPrices,
            };
            function isKeyRange(key, min, max) {
                const no = parseInt(key);
                return no >= min && no < max;
            }
        });
    }
    getBidderBalance(tld, address) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.validator.supportedTLDs.includes(tld)) {
                throw new Error(`TLD '${tld}' is not supported.`);
            }
            const contractAddress = yield this.addressBook.lookup(SmartContractType.TLDRegistrar, tld);
            const balanceResponse = yield this.tezos.getBigMapValue(contractAddress, s => s.store.bidder_balances, RpcRequestData.fromValue(address));
            return balanceResponse.scalar(BigNumberEncoder) || 0;
        });
    }
    getTokenId(name) {
        return __awaiter(this, void 0, void 0, function* () {
            this.assertDomainName(name);
            if (getLevel(name) !== 2) {
                throw new Error(`Domain '${name}' does not have a tokenId. Only 2nd level domains (e.g. 'alice.${this.validator.supportedTLDs[0]}') are NFTs.`);
            }
            const record = yield this.bigMapDataProvider.getDomainRecord(name);
            return (record === null || record === void 0 ? void 0 : record.tzip12_token_id) || null;
        });
    }
    getDomainRecord(name) {
        return __awaiter(this, void 0, void 0, function* () {
            this.assertDomainName(name);
            const record = yield this.bigMapDataProvider.getDomainRecord(name);
            return record !== null && record !== void 0 ? record : null;
        });
    }
    assertDomainName(name) {
        const validation = this.validator.validateDomainName(name);
        if (validation === DomainNameValidationResult.VALID) {
            return;
        }
        this.tracer.trace('!! Domain name validation failed.', validation);
        throw new Error(`'${name}' is not a valid domain name.`);
    }
    assertDomainWithValidTld(name) {
        const validation = this.validator.isValidWithKnownTld(name);
        if (validation === DomainNameValidationResult.VALID) {
            return;
        }
        this.tracer.trace('!! Domain name validation failed.', validation);
        throw new Error(`'${name}' is not a valid domain name.`);
    }
}

class TaquitoTezosDomainsOperationFactory {
    constructor(tezos, addressBook, tracer, commitmentGenerator, dataProvider, validator) {
        this.tezos = tezos;
        this.addressBook = addressBook;
        this.tracer = tracer;
        this.commitmentGenerator = commitmentGenerator;
        this.dataProvider = dataProvider;
        this.validator = validator;
    }
    setChildRecord(request, operationParams) {
        return __awaiter(this, void 0, void 0, function* () {
            this.assertDomainName(`${request.label}.${request.parent}`);
            const entrypoint = 'set_child_record';
            this.tracer.trace(`=> Preparing operation ${entrypoint}.`, request);
            const address = yield this.addressBook.lookup(SmartContractType.NameRegistry, entrypoint);
            const encodedRequest = RpcRequestData.fromObject(SetChildRecordRequest, request).encode();
            const params = yield this.tezos.params(address, entrypoint, [encodedRequest.label, encodedRequest.parent, encodedRequest.address, encodedRequest.owner, encodedRequest.data, encodedRequest.expiry], Object.assign({ storageLimit: DEFAULT_STORAGE_LIMITS[entrypoint] }, operationParams));
            this.tracer.trace('<= Prepared.', params);
            return params;
        });
    }
    updateRecord(request, operationParams) {
        return __awaiter(this, void 0, void 0, function* () {
            this.assertDomainName(request.name);
            const entrypoint = 'update_record';
            this.tracer.trace(`=> Preparing operation ${entrypoint}.`, request);
            const address = yield this.addressBook.lookup(SmartContractType.NameRegistry, entrypoint);
            const encodedRequest = RpcRequestData.fromObject(UpdateRecordRequest, request).encode();
            const params = yield this.tezos.params(address, entrypoint, [encodedRequest.name, encodedRequest.address, encodedRequest.owner, encodedRequest.data], Object.assign({ storageLimit: DEFAULT_STORAGE_LIMITS[entrypoint] }, operationParams));
            this.tracer.trace('<= Prepared.', params);
            return params;
        });
    }
    commit(tld, request, operationParams) {
        return __awaiter(this, void 0, void 0, function* () {
            this.assertDomainWithValidTld(`${request.label}.${tld}`);
            const entrypoint = 'commit';
            this.tracer.trace(`=> Preparing operation ${entrypoint}.`, request);
            const address = yield this.addressBook.lookup(SmartContractType.TLDRegistrar, tld, entrypoint);
            const commitmentHash = this.commitmentGenerator.generate(request);
            const params = yield this.tezos.params(address, entrypoint, [commitmentHash], Object.assign({ storageLimit: DEFAULT_STORAGE_LIMITS[entrypoint] }, operationParams));
            this.tracer.trace('<= Prepared.', params);
            return params;
        });
    }
    buy(tld, request, operationParams) {
        return __awaiter(this, void 0, void 0, function* () {
            this.assertDomainWithValidTld(`${request.label}.${tld}`);
            const entrypoint = 'buy';
            this.tracer.trace(`=> Preparing operation ${entrypoint}.`, request);
            const info = yield this.dataProvider.getAcquisitionInfo(`${request.label}.${tld}`);
            const price = info.calculatePrice(request.duration);
            const address = yield this.addressBook.lookup(SmartContractType.TLDRegistrar, tld, entrypoint);
            const encodedRequest = RpcRequestData.fromObject(BuyRequest, request).encode();
            const params = yield this.tezos.params(address, entrypoint, [encodedRequest.label, encodedRequest.duration, encodedRequest.owner, encodedRequest.address, encodedRequest.data, encodedRequest.nonce], Object.assign({ amount: price, storageLimit: DEFAULT_STORAGE_LIMITS[entrypoint] }, operationParams));
            this.tracer.trace('<= Prepared.', params);
            return params;
        });
    }
    renew(tld, request, operationParams) {
        return __awaiter(this, void 0, void 0, function* () {
            this.assertDomainWithValidTld(`${request.label}.${tld}`);
            const entrypoint = 'renew';
            this.tracer.trace(`=> Preparing operation ${entrypoint}.`, request);
            const info = yield this.dataProvider.getAcquisitionInfo(`${request.label}.${tld}`);
            const price = info.calculatePrice(request.duration);
            const address = yield this.addressBook.lookup(SmartContractType.TLDRegistrar, tld, entrypoint);
            const encodedRequest = RpcRequestData.fromObject(RenewRequest, request).encode();
            const params = yield this.tezos.params(address, entrypoint, [encodedRequest.label, encodedRequest.duration], Object.assign({ amount: price }, operationParams));
            this.tracer.trace('<= Prepared.', params);
            return params;
        });
    }
    claimReverseRecord(request, operationParams) {
        return __awaiter(this, void 0, void 0, function* () {
            if (request.name) {
                this.assertDomainName(request.name);
            }
            const entrypoint = 'claim_reverse_record';
            this.tracer.trace(`=> Preparing operation ${entrypoint}.`, request);
            const address = yield this.addressBook.lookup(SmartContractType.NameRegistry, entrypoint);
            const encodedRequest = RpcRequestData.fromObject(ReverseRecordRequest, request).encode();
            const params = yield this.tezos.params(address, entrypoint, [encodedRequest.name, encodedRequest.owner], Object.assign({ storageLimit: DEFAULT_STORAGE_LIMITS[entrypoint] }, operationParams));
            this.tracer.trace('<= Prepared.', params);
            return params;
        });
    }
    updateReverseRecord(request, operationParams) {
        return __awaiter(this, void 0, void 0, function* () {
            if (request.name) {
                this.assertDomainName(request.name);
            }
            const entrypoint = 'update_reverse_record';
            this.tracer.trace(`=> Preparing operation ${entrypoint}.`, request);
            const address = yield this.addressBook.lookup(SmartContractType.NameRegistry, entrypoint);
            const encodedRequest = RpcRequestData.fromObject(UpdateReverseRecordRequest, request).encode();
            const params = yield this.tezos.params(address, entrypoint, [encodedRequest.address, encodedRequest.name, encodedRequest.owner], Object.assign({ storageLimit: DEFAULT_STORAGE_LIMITS[entrypoint] }, operationParams));
            this.tracer.trace('<= Prepared.', params);
            return params;
        });
    }
    bid(tld, request, operationParams) {
        return __awaiter(this, void 0, void 0, function* () {
            this.assertDomainWithValidTld(`${request.label}.${tld}`);
            const entrypoint = 'bid';
            this.tracer.trace(`=> Preparing operation ${entrypoint}.`, request);
            const balance = yield this.dataProvider.getBidderBalance(tld, yield this.tezos.getPkh());
            const address = yield this.addressBook.lookup(SmartContractType.TLDRegistrar, tld, entrypoint);
            const encodedRequest = RpcRequestData.fromObject(BidRequest, request).encode();
            const params = yield this.tezos.params(address, entrypoint, [encodedRequest.label, encodedRequest.bid], Object.assign({ amount: Math.max(0, request.bid - balance), storageLimit: DEFAULT_STORAGE_LIMITS[entrypoint] }, operationParams));
            this.tracer.trace('<= Prepared.', params);
            return params;
        });
    }
    settle(tld, request, operationParams) {
        return __awaiter(this, void 0, void 0, function* () {
            this.assertDomainWithValidTld(`${request.label}.${tld}`);
            const entrypoint = 'settle';
            this.tracer.trace(`=> Preparing operation ${entrypoint}.`, request);
            const address = yield this.addressBook.lookup(SmartContractType.TLDRegistrar, tld, entrypoint);
            const encodedRequest = RpcRequestData.fromObject(SettleRequest, request).encode();
            const params = yield this.tezos.params(address, entrypoint, [encodedRequest.label, encodedRequest.owner, encodedRequest.address, encodedRequest.data], Object.assign({ storageLimit: DEFAULT_STORAGE_LIMITS[entrypoint] }, operationParams));
            this.tracer.trace('<= Prepared.', params);
            return params;
        });
    }
    withdraw(tld, recipient, operationParams) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.validator.supportedTLDs.includes(tld)) {
                throw new Error(`TLD '${tld}' is not supported.`);
            }
            const entrypoint = 'withdraw';
            this.tracer.trace(`=> Preparing operation ${entrypoint}.`, recipient);
            const address = yield this.addressBook.lookup(SmartContractType.TLDRegistrar, tld, entrypoint);
            const params = yield this.tezos.params(address, entrypoint, [recipient], operationParams);
            this.tracer.trace('<= Prepared.', params);
            return params;
        });
    }
    transfer(name, newOwner, operationParams) {
        return __awaiter(this, void 0, void 0, function* () {
            const domain = yield this.dataProvider.getDomainRecord(name);
            if (!(domain === null || domain === void 0 ? void 0 : domain.tzip12_token_id)) {
                throw new Error(`FA2 token id not found for domain ${name}.`);
            }
            const entrypoint = 'transfer';
            const address = yield this.addressBook.lookup(SmartContractType.NameRegistry);
            const request = RpcRequestData.fromObject(Tzip12TransferRequest, {
                from_: domain.owner,
                txs: [
                    RpcRequestData.fromObject(Tzip12TransferDestination, {
                        to_: newOwner,
                        amount: 1,
                        token_id: domain.tzip12_token_id,
                    }).encode(),
                ],
            }).encode();
            this.tracer.trace(`=> Preparing operation ${entrypoint}.`, name, newOwner);
            const params = yield this.tezos.params(address, entrypoint, [[request]], operationParams);
            this.tracer.trace('<= Prepared.', params);
            return params;
        });
    }
    claim(signature, request, operationParams) {
        return __awaiter(this, void 0, void 0, function* () {
            this.assertDomainWithValidTld(`${request.label}.${request.tld}`);
            const entrypoint = 'claim';
            this.tracer.trace(`=> Preparing operation ${entrypoint}.`, request);
            const tldConfig = yield this.dataProvider.getTldConfiguration(request.tld);
            const price = tldConfig.claimPrice;
            const address = yield this.addressBook.lookup(SmartContractType.OracleRegistrar);
            const encodedRequest = RpcRequestData.fromObject(ClaimRequest, request).encode();
            const params = yield this.tezos.params(address, entrypoint, [encodedRequest.label, encodedRequest.tld, encodedRequest.owner, encodedRequest.timestamp, signature], Object.assign({ amount: price === null || price === void 0 ? void 0 : price.toNumber() }, operationParams));
            this.tracer.trace('<= Prepared.', params);
            return params;
        });
    }
    assertDomainName(name) {
        const validation = this.validator.validateDomainName(name);
        if (validation === DomainNameValidationResult.VALID) {
            return;
        }
        this.tracer.trace('!! Domain name validation failed.', validation);
        throw new Error(`'${name}' is not a valid domain name.`);
    }
    assertDomainWithValidTld(name) {
        const validation = this.validator.isValidWithKnownTld(name);
        if (validation === DomainNameValidationResult.VALID) {
            return;
        }
        this.tracer.trace('!! Domain name validation failed.', validation);
        throw new Error(`'${name}' is not a valid domain name.`);
    }
}

class UnsupportedDomainsManager {
    setChildRecord() {
        throw new NotSupportedError();
    }
    updateRecord() {
        throw new NotSupportedError();
    }
    commit() {
        throw new NotSupportedError();
    }
    buy() {
        throw new NotSupportedError();
    }
    renew() {
        throw new NotSupportedError();
    }
    claimReverseRecord() {
        throw new NotSupportedError();
    }
    updateReverseRecord() {
        throw new NotSupportedError();
    }
    getCommitment() {
        throw new NotSupportedError();
    }
    getAcquisitionInfo() {
        throw new NotSupportedError();
    }
    getBidderBalance() {
        throw new NotSupportedError();
    }
    getTldConfiguration() {
        throw new NotSupportedError();
    }
    bid() {
        throw new NotSupportedError();
    }
    settle() {
        throw new NotSupportedError();
    }
    withdraw() {
        throw new NotSupportedError();
    }
    getTokenId() {
        throw new NotSupportedError();
    }
    transfer() {
        throw new NotSupportedError();
    }
    batch() {
        throw new NotSupportedError();
    }
}

export { BidRequest, BlockchainDomainsManager, BuyRequest, ClaimRequest, CommitmentGenerator, CommitmentInfo, CommitmentRequest, DomainAcquisitionInfo, DomainAcquisitionState, RenewRequest, ReverseRecordRequest, SetChildRecordRequest, SettleRequest, TaquitoManagerDataProvider, TaquitoTezosDomainsOperationFactory, UnsupportedDomainsManager, UpdateRecordRequest, UpdateReverseRecordRequest, calculateAcquisitionInfo };
//# sourceMappingURL=manager.es2015.js.map
